import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Eye, Loader2, CheckCircle2, BookOpen, Info,
  AlertTriangle, CircleSlash, XCircle,
} from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { cn } from '@/utils/cn';
import {
  sampleDataApi,
  type SampleDataArea, type SampleDataPlan, type SampleDataResult,
} from '@/api/sampleData.api';

/**
 * Fills the ledger with demonstration data that is actually correct accounting.
 *
 * The twin of Demo Reset — that empties a company, this fills it. Everything here is written through
 * the same commands a person uses, so the journals post, the open items age, the document numbers
 * come off the real sequences, and a trial balance over the result balances.
 */
export const SampleDataPage = () => {
  const { hasPermission } = usePermission();
  const canGenerate = hasPermission(Permissions.SAMPLE_DATA_GENERATE);

  const [areas, setAreas]         = useState<SampleDataArea[]>([]);
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [plan, setPlan]           = useState<SampleDataPlan | null>(null);
  const [result, setResult]       = useState<SampleDataResult | null>(null);
  const [confirm, setConfirm]     = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    sampleDataApi.areas()
      .then(a => { setAreas(a); setSelected(new Set(a.map(x => x.key))); })
      .catch(() => {});
  }, []);

  const toggle = (key: string) => {
    setPlan(null); setResult(null);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => { setPlan(null); setResult(null); setSelected(new Set(areas.map(a => a.key))); };
  const selectNone = () => { setPlan(null); setResult(null); setSelected(new Set()); };

  const runPreview = useCallback(async () => {
    setError(null); setResult(null); setPreviewing(true);
    try { setPlan(await sampleDataApi.preview([...selected])); }
    catch (e: any) { setError(e?.response?.data?.message ?? 'Preview failed.'); }
    finally { setPreviewing(false); }
  }, [selected]);

  const companyCode = plan?.companyCode ?? '';
  const canRun = canGenerate && selected.size > 0 && !!plan
    && confirm.trim().toUpperCase() === companyCode.toUpperCase();

  const runGenerate = async () => {
    if (!canRun) return;
    setError(null); setGenerating(true);
    try {
      const res = await sampleDataApi.generate([...selected], confirm.trim());
      setResult(res); setPlan(null); setConfirm('');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Generation failed.');
    } finally { setGenerating(false); }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-auto">
      <div className="px-6 py-5 max-w-5xl w-full mx-auto space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Sparkles size={20} className="text-signal" /> Sample Data
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Fill this company with realistic, financially-correct demonstration transactions — every screen, every combination.
            </p>
          </div>
          <Link to={AppRoutes.ADMIN_SAMPLE_DATA_GUIDE}
            className="flex-shrink-0 px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
            <BookOpen size={14} /> Read the guide
          </Link>
        </div>

        {/* What this actually does */}
        <div className="flex gap-3 rounded-lg border border-sky-200 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-800/50 px-4 py-3">
          <Info size={18} className="text-sky-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-sky-900 dark:text-sky-300 space-y-1">
            <p>
              <span className="font-semibold">This is real accounting, not filler.</span> Every document is
              written through the same commands a person uses, so it posts real journals, ages real open items,
              consumes real document numbers, and has to pass the same validations. A trial balance over the
              result balances.
            </p>
            <p className="text-sky-800/90 dark:text-sky-400/90">
              It is safe to re-run: an area already seeded is skipped, never duplicated. To start over,
              use <Link to={AppRoutes.ADMIN_DEMO_RESET} className="underline font-medium">Demo Reset</Link> first.
            </p>
          </div>
        </div>

        {!canGenerate && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            You can view the areas and the guide, but you do not have permission to generate sample data. Ask an administrator if you need it.
          </div>
        )}

        {/* 1. Areas */}
        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">1. Choose what to generate</h2>
            <div className="flex gap-2 text-xs">
              <button onClick={selectAll}  className="text-signal hover:underline">Select all</button>
              <span className="text-slate-300">·</span>
              <button onClick={selectNone} className="text-slate-500 hover:underline">None</button>
            </div>
          </div>
          <div className="space-y-2.5">
            {areas.map(a => (
              <label key={a.key}
                className={cn('flex gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                  selected.has(a.key)
                    ? 'border-signal bg-signal/5'
                    : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/40')}>
                <input type="checkbox" checked={selected.has(a.key)} onChange={() => toggle(a.key)}
                  className="mt-0.5 accent-signal" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{a.name}</span>
                    <span className="text-[10px] font-mono text-slate-400">{a.key}</span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{a.description}</div>
                  <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                    <span>{a.scenarioCount} documented scenario{a.scenarioCount === 1 ? '' : 's'}</span>
                    {a.dependsOn.length > 0 && (
                      <span>· needs {a.dependsOn.join(', ')}</span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={runPreview} disabled={selected.size === 0 || previewing}
              className="px-3 py-2 text-sm font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 disabled:opacity-40 flex items-center gap-1.5">
              {previewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview
            </button>
            <span className="text-xs text-slate-400">Read-only — reports what is already there and what would be added.</span>
          </div>
        </section>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700/50 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        {/* 2. Plan */}
        {plan && (
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">2. What would be created — {plan.companyCode}</h2>
            <div className="mt-2 mb-4">
              <span className="text-3xl font-bold text-signal">{plan.totalPlanned.toLocaleString()}</span>
              <span className="ml-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">document(s) to create</span>
            </div>

            {plan.issues.length > 0 && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-3 py-2">
                {plan.issues.map((i, n) => (
                  <div key={n} className="text-xs text-amber-800 dark:text-amber-300 flex gap-1.5">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />{i}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1.5">
              {plan.areas.map(a => (
                <div key={a.key} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-100 dark:border-slate-700">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{a.name}</span>
                    {a.notes.map((n, i) => (
                      <div key={i} className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{n}</div>
                    ))}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {a.alreadySeeded
                      ? <span className="text-xs font-medium text-slate-400 flex items-center gap-1"><CircleSlash size={12} /> already seeded</span>
                      : <span className="font-mono text-sm text-signal">+{a.plannedCount.toLocaleString()}</span>}
                    {a.existingCount > 0 && !a.alreadySeeded && (
                      <div className="text-[10px] text-slate-400">{a.existingCount} already there</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 3. Confirm */}
            {canGenerate && (
              <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">3. Confirm</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Type the company code <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{plan.companyCode}</span> — this writes a lot of financial data into it.
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={plan.companyCode}
                    className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal/40 w-48" />
                  <button onClick={runGenerate} disabled={!canRun || generating}
                    className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:brightness-110 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                    {generating
                      ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
                      : <><Sparkles size={14} /> Generate sample data</>}
                  </button>
                </div>
                {generating && (
                  <p className="text-xs text-slate-400 mt-2">
                    Each document is submitted, approved and posted in turn. This takes a little while — don't navigate away.
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {/* Result */}
        {result && (
          <section className={cn('rounded-xl border p-5',
            result.status === 'COMPLETED'
              ? 'border-teal-300 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-700/50'
              : 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50')}>
            <h2 className={cn('text-sm font-semibold flex items-center gap-1.5',
              result.status === 'COMPLETED' ? 'text-teal-800 dark:text-teal-300' : 'text-amber-800 dark:text-amber-300')}>
              {result.status === 'COMPLETED' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              {result.totalCreated.toLocaleString()} document(s) created
              {result.totalSkipped > 0 && ` · ${result.totalSkipped.toLocaleString()} skipped`}
            </h2>

            {result.issues.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-3 py-2">
                {result.issues.map((i, n) => (
                  <div key={n} className="text-xs text-amber-800 dark:text-amber-300 flex gap-1.5">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />{i}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-1.5">
              {result.areas.map(a => (
                <div key={a.key} className="rounded-lg bg-white/60 dark:bg-slate-800/60 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                      {a.status === 'OK'      && <CheckCircle2 size={13} className="text-teal-600" />}
                      {a.status === 'SKIPPED' && <CircleSlash  size={13} className="text-slate-400" />}
                      {a.status === 'FAILED'  && <XCircle      size={13} className="text-red-500" />}
                      {a.name}
                    </span>
                    <span className="font-mono text-xs text-slate-500">
                      {a.created > 0 ? `+${a.created}` : a.status.toLowerCase()}
                    </span>
                  </div>
                  {a.messages.map((m, i) => (
                    <div key={i} className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 ml-5">{m}</div>
                  ))}
                  {a.errors.map((e, i) => (
                    <div key={i} className="text-xs text-red-600 dark:text-red-400 mt-0.5 ml-5">{e}</div>
                  ))}
                </div>
              ))}
            </div>

            <Link to={AppRoutes.ADMIN_SAMPLE_DATA_GUIDE}
              className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-signal hover:underline">
              <BookOpen size={14} /> Now read what each of these is meant to show you
            </Link>
          </section>
        )}
      </div>
    </div>
  );
};
